import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { ClientRow } from '../api';
import { memberNo, won } from '../format';

/*
 * 이용자 목록: 통합 검색 · 표시 개수 · 페이지 · 열 너비 조절(끌기/키보드, 이 브라우저에 기억) · 목표 전체 보기.
 * 민감정보(연락처·주소·장애정보·메모)는 목록에 싣지 않는다 — 목록을 열 때마다 모든 아동의 민감정보를
 * 복호화하면 열람 범위가 넓어지고 감사 기록이 무의미해진다. 민감정보는 수정 창에서 '불러오기'로 한 명씩 본다.
 */

interface Col { id: 'id' | 'name' | 'year' | 'staff' | 'goals' | 'price' | 'action'; label: string; min: number; base: number }
const COLUMNS: Col[] = [
  { id: 'id', label: '회원번호', min: 84, base: 96 },
  { id: 'name', label: '이름', min: 112, base: 150 },
  { id: 'year', label: '출생연도', min: 86, base: 92 },
  { id: 'staff', label: '담당 선생님', min: 110, base: 160 },
  { id: 'goals', label: '서비스 목표', min: 180, base: 360 },
  { id: 'price', label: '회기 단가', min: 100, base: 112 },
  { id: 'action', label: '관리', min: 80, base: 82 },
];
const WIDTH_KEY = 'selfon.clients.columns.v1';
const PAGE_SIZES = [10, 25, 50, 100];

/** 한글·영문 대소문자·공백·기호 차이를 무시하고 비교 */
const norm = (v: unknown) => String(v ?? '').normalize('NFKC').toLocaleLowerCase('ko-KR').replace(/[\s\-().,]+/g, '');

function loadWidths(): Record<string, number> {
  try { const v = JSON.parse(localStorage.getItem(WIDTH_KEY) || '{}'); return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; } catch { return {}; }
}

/** 저장된 너비는 그대로, 나머지 열은 남은 공간을 기본 비율로 나눈다 */
function widthsFor(cols: Col[], saved: Record<string, number>, container: number) {
  const fixedIds = cols.filter((c) => Number.isFinite(saved[c.id]));
  const fixed = fixedIds.reduce((n, c) => n + Math.max(c.min, Math.min(720, saved[c.id]!)), 0);
  const auto = cols.filter((c) => !Number.isFinite(saved[c.id]));
  const base = auto.reduce((n, c) => n + c.base, 0);
  const extra = container > 0 ? Math.max(0, container - fixed - base) : 0;
  return cols.map((c) => (Number.isFinite(saved[c.id]) ? Math.max(c.min, Math.min(720, saved[c.id]!)) : c.base + (base ? (extra * c.base) / base : 0)));
}

function Highlight({ text, words }: { text: string; words: string[] }): ReactNode {
  if (!words.length || !text) return text;
  const lower = text.toLocaleLowerCase('ko-KR');
  const hit = words.map((w) => ({ w, i: lower.indexOf(w) })).find((x) => x.i >= 0 && x.w);
  if (!hit) return text;
  return <>{text.slice(0, hit.i)}<mark>{text.slice(hit.i, hit.i + hit.w.length)}</mark>{text.slice(hit.i + hit.w.length)}</>;
}

export function ClientDirectory({ clients, staffName, canEdit, onEdit }: {
  clients: ClientRow[]; staffName: Map<string, string>; canEdit: boolean; onEdit: (c: ClientRow) => void;
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(false);
  const [saved, setSaved] = useState<Record<string, number>>(loadWidths);
  const [container, setContainer] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const cols = COLUMNS.filter((c) => c.id !== 'action' || canEdit).filter((c) => c.id !== 'price' || clients.some((x) => x.unitPrice !== undefined));
  const widths = widthsFor(cols, saved, container);

  useEffect(() => { try { localStorage.setItem(WIDTH_KEY, JSON.stringify(saved)); } catch { /* 저장 불가 환경 */ } }, [saved]);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const update = () => setContainer(el.clientWidth);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const teachers = (c: ClientRow) => (c.therapistIds ?? []).map((id) => staffName.get(id) ?? '퇴사/미등록');
  const words = query.trim().split(/\s+/u).map(norm).filter(Boolean);
  const rows = useMemo(() => {
    const sorted = [...clients].sort((a, b) => a.displayName.localeCompare(b.displayName, 'ko'));
    if (!words.length) return sorted;
    return sorted.filter((c) => {
      const fields = [memberNo(c.id), c.displayName, c.birthYear, ...teachers(c), ...(c.goals ?? []).map((g) => g.text)].map(norm);
      return words.every((w) => fields.some((f) => f.includes(w)));
    });
  }, [clients, query, staffName]); // eslint-disable-line react-hooks/exhaustive-deps
  const pages = Math.max(1, Math.ceil(rows.length / pageSize));
  const cur = Math.min(page, pages);
  const shown = rows.slice((cur - 1) * pageSize, cur * pageSize);
  useEffect(() => setPage(1), [query, pageSize]);

  const startResize = (i: number, e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const col = cols[i]!;
    const x0 = e.clientX, w0 = widths[i]!;
    const move = (ev: PointerEvent) => setSaved((s) => ({ ...s, [col.id]: Math.round(Math.max(col.min, Math.min(720, w0 + ev.clientX - x0))) }));
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const keyResize = (i: number, key: string) => {
    const col = cols[i]!;
    if (key === 'Home') { setSaved((s) => { const n = { ...s }; delete n[col.id]; return n; }); return; }
    const d = key === 'ArrowLeft' ? -16 : key === 'ArrowRight' ? 16 : 0;
    if (d) setSaved((s) => ({ ...s, [col.id]: Math.round(Math.max(col.min, Math.min(720, widths[i]! + d))) }));
  };
  const resetOne = (i: number) => setSaved((s) => { const n = { ...s }; delete n[cols[i]!.id]; return n; });

  const cell = (c: ClientRow, id: Col['id']): ReactNode => {
    switch (id) {
      case 'id': return <span className="client-cell-id">{memberNo(c.id)}</span>;
      case 'name': return <div className="client-name-cell"><strong><Highlight text={c.displayName} words={words} /></strong>{c.hasSensitive && <span className="client-private-badge">민감정보 있음</span>}</div>;
      case 'year': return <span className="client-cell-year"><Highlight text={String(c.birthYear)} words={words} /></span>;
      case 'staff': { const t = teachers(c); return t.length ? <Highlight text={t.join(', ')} words={words} /> : <span className="muted">미지정</span>; }
      case 'goals': return c.goals?.length ? <div className="client-cell-content">{c.goals.map((g, j) => <div key={g.id ?? j}><span className="muted small">{g.term === 'SHORT' ? '단기' : '장기'} · </span><Highlight text={g.text} words={words} /></div>)}</div> : <span className="muted">-</span>;
      case 'price': return <span className="client-cell-price">{c.unitPrice !== undefined ? won(c.unitPrice) : '-'}</span>;
      case 'action': return <button type="button" className="btn btn-ghost btn-sm" onClick={() => onEdit(c)} aria-label={`${c.displayName} 정보 수정`}>수정</button>;
    }
  };

  return (
    <section className="client-directory" aria-label="이용자 검색 및 목록">
      <div className="client-search-panel">
        <div className="client-search-field">
          <label htmlFor="client-search">이용자 검색</label>
          <div className="client-search-box">
            <span aria-hidden="true">⌕</span>
            <input id="client-search" type="search" placeholder="이름, 회원번호, 출생연도, 담당 선생님, 목표" value={query} onChange={(e) => setQuery(e.target.value)} aria-describedby="client-search-hint" />
            {query && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuery('')} aria-label="검색어 지우기">지우기</button>}
          </div>
          <p className="hint" id="client-search-hint">띄어쓰기로 여러 조건 검색</p>
        </div>
        <p className="client-count" aria-live="polite"><strong>{rows.length}</strong>명<span>전체 {clients.length}명</span></p>
      </div>

      <div className="client-table-tools">
        <label className="client-page-size">표시 개수
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="페이지당 이용자 수">
            {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}명</option>)}
          </select>
        </label>
        <div className="client-table-actions">
          <button type="button" className="btn btn-outline btn-sm" aria-pressed={expanded} onClick={() => setExpanded((v) => !v)}>{expanded ? '목표 접기' : '목표 전체 보기'}</button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSaved({})}>열 너비 초기화</button>
        </div>
      </div>

      <div className="client-scroll" ref={wrap} tabIndex={0} aria-label="이용자 표, 좁은 화면에서는 가로로 스크롤">
        <table className={`client-table${expanded ? ' client-expanded' : ''}`} style={{ width: widths.reduce((a, b) => a + b, 0) }}>
          <caption className="sr-only">이용자 목록. 열 제목 오른쪽 경계를 끌거나 ← → 키로 너비를 조절할 수 있습니다.</caption>
          <colgroup>{cols.map((c, i) => <col key={c.id} style={{ width: widths[i] }} />)}</colgroup>
          <thead>
            <tr>
              {cols.map((c, i) => (
                <th key={c.id} scope="col" style={{ position: 'sticky' }}>
                  {c.label}
                  {c.id !== 'action' && (
                    <button type="button" className="client-col-resizer" aria-label={`${c.label} 열 너비 조절`} title="끌어서 너비 조절 · 더블클릭하면 초기화"
                      onPointerDown={(e) => startResize(i, e)} onDoubleClick={() => resetOne(i)} onKeyDown={(e) => { if (['ArrowLeft', 'ArrowRight', 'Home'].includes(e.key)) { e.preventDefault(); keyResize(i, e.key); } }} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => <tr key={c.id}>{cols.map((col) => <td key={col.id} className={`client-cell-${col.id}`}>{cell(c, col.id)}</td>)}</tr>)}
            {shown.length === 0 && (
              <tr><td className="client-empty" colSpan={cols.length}>
                <strong>{query ? '검색 결과가 없습니다.' : '등록된 이용자가 없습니다.'}</strong>
                <p>{query ? '다른 검색어로 찾아보세요.' : '이용자를 등록하면 이곳에서 확인할 수 있습니다.'}</p>
                {query && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuery('')}>검색어 지우기</button>}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="client-pagination">
        <span>{rows.length ? `${(cur - 1) * pageSize + 1}–${Math.min(cur * pageSize, rows.length)} / ${rows.length}명` : '0명'}</span>
        <nav aria-label="이용자 목록 페이지">
          <button type="button" className="btn btn-ghost btn-sm" disabled={cur <= 1} onClick={() => setPage(cur - 1)}>이전</button>
          <span aria-current="page">{cur} / {pages}</span>
          <button type="button" className="btn btn-ghost btn-sm" disabled={cur >= pages} onClick={() => setPage(cur + 1)}>다음</button>
        </nav>
      </div>
    </section>
  );
}
